import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Loader2, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertCircle,
  Download,
  FileText,
  Calendar,
  DollarSign,
  Zap,
  StopCircle,
  PauseCircle,
  RefreshCw,
  ClipboardCheck,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { jobsAPI, dimensionsAPI } from '../services/api';
import type { ProcessingJob, WritebackError, ResumeJobResponse, PartCategoryProcessingDetails } from '../types';
import { isDimensionProcessingStatus, isPartCategoryJob } from '../types';
import { DimensionProcessingStatusComponent } from '../components/dimensions/DimensionProcessingStatus';
import { PartCategoryProcessingStatusComponent } from '../components/partCategory/PartCategoryProcessingStatus';
import { PartCategoryCostBreakdown } from '../components/partCategory/PartCategoryCostBreakdown';
import { theme } from '../styles/theme';
import { extractApiError } from '../utils/errorMessages';
import { useConfirm } from '../contexts/ConfirmContext';
import { useToast } from '../hooks/use-toast';
import { isPreTerminalStatus } from '../utils/jobStatus';

// ── Module-level helpers ─────────────────────────────────────────────────────

/** How often a visible page asks for fresh job data. */
const POLL_INTERVAL_MS = 3000;

/** Ceiling on the exponential back-off after consecutive failures (ADR-048).
 *  30 s keeps a recovering server's first successful answer soon enough that
 *  the page repairs itself while the user is still looking at it. */
const MAX_POLL_BACKOFF_MS = 30_000;

/**
 * HTTP status of a failed request, or undefined for a transport-level failure
 * (no response at all: DNS, TLS, timeout, offline, a restarting backend).
 *
 * Decision: PDR-047 — this is the whole basis for telling "this job does not
 * exist" apart from "we could not reach the server just now", which the page
 * used to conflate into one permanent "Job Not Found".
 * See docs/decisions/PDR-047-a-failed-refresh-keeps-the-last-known-job.md
 */
const errorStatus = (err: unknown): number | undefined =>
  err !== null && typeof err === 'object' && 'response' in err
    ? (err as { response?: { status?: number } }).response?.status
    : undefined;

/** A status that means the job is genuinely not there for this user, so retrying
 *  cannot help and the not-found screen is the honest answer. */
const isDefinitivelyGone = (status: number | undefined): boolean =>
  status === 404 || status === 403;

// ── Module-level download utilities ──────────────────────────────────────────

const triggerAnchorDownload = (url: string, filename: string): void => {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const triggerBlobDownload = (data: BlobPart, filename: string, mimeType: string): void => {
  const blobUrl = window.URL.createObjectURL(new Blob([data], { type: mimeType }));
  triggerAnchorDownload(blobUrl, filename);
  window.URL.revokeObjectURL(blobUrl);
};

// ── Shared sub-components ─────────────────────────────────────────────────────

interface DownloadButtonProps {
  onClick: () => void;
  loading: boolean;
  icon: React.ElementType;
  label: string;
  loadingLabel?: string;
  className: string;
  'data-testid'?: string;
}

const DownloadButton: React.FC<DownloadButtonProps> = ({
  onClick,
  loading,
  icon: Icon,
  label,
  loadingLabel = 'Downloading...',
  className,
  'data-testid': testId,
}) => (
  <button onClick={onClick} disabled={loading} data-testid={testId} className={className}>
    {loading
      ? <><Loader2 className="w-4 h-4 animate-spin" /><span>{loadingLabel}</span></>
      : <><Icon className="w-4 h-4" /><span>{label}</span></>
    }
  </button>
);

interface ResumeJobButtonProps {
  onClick: () => void;
  loading: boolean;
  canResume: boolean;
  'data-testid'?: string;
  className?: string;
}

const ResumeJobButton: React.FC<ResumeJobButtonProps> = ({
  onClick,
  loading,
  canResume,
  'data-testid': testId,
  className = 'inline-flex items-center gap-2 px-4 py-2 bg-qc-success hover:bg-qc-success/90 text-text-inverse text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
}) => (
  <button
    onClick={onClick}
    disabled={loading || !canResume}
    title={!canResume ? 'No checkpoint found — cannot resume from this point' : undefined}
    data-testid={testId}
    className={className}
  >
    {loading
      ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Resuming...</span></>
      : <><RefreshCw className="w-4 h-4" /><span>Resume Job</span></>
    }
  </button>
);

const JobDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const confirm = useConfirm();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [job, setJob] = useState<ProcessingJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Set when a fetch failed for a reason that retrying can fix. Unlike `error`
  // it is advisory: the page keeps rendering whatever it already has and shows
  // a reconnecting note, because a background refresh failing says nothing
  // about whether the job exists (PDR-047).
  const [unreachable, setUnreachable] = useState(false);
  const [activeDownload, setActiveDownload] = useState<string | null>(null);
  const [resuming, setResuming] = useState(false);
  const [errorLogOpen, setErrorLogOpen] = useState(false);

  // Refs, not deps: the poll must read the *current* job and error without
  // tearing the interval down and rebuilding it on every status change.
  const jobRef = useRef<ProcessingJob | null>(null);
  const errorRef = useRef('');
  const inFlightRef = useRef(false);
  // Exponential back-off state. `nextAttemptAt` is an epoch ms floor, so a tick
  // that arrives too early is dropped rather than rescheduling the interval.
  const failureCountRef = useRef(0);
  const nextAttemptAtRef = useRef(0);
  jobRef.current = job;
  errorRef.current = error;

  useEffect(() => {
    if (!id) return;

    fetchJobDetail();

    // A tab nobody is looking at costs nothing, and coming back to one is
    // instant. This is the "I leave it away for a while" half of the report: a
    // backgrounded tab was issuing 20 requests a minute for hours (ADR-048).
    const onVisible = () => {
      if (document.hidden) return;
      // Returning to the tab is the one moment the user is definitely waiting,
      // so clear any back-off and fetch now instead of up to 3 s later.
      nextAttemptAtRef.current = 0;
      if (!errorRef.current) fetchJobDetail(true);
    };
    document.addEventListener('visibilitychange', onVisible);

    // Auto-refresh every 3 seconds while the job is still making progress on
    // its own. Any pre-terminal status polls — including `pending`, which is
    // reachable during the reconcile window and previously froze the page
    // (M7). Terminal states and states awaiting user action (suspended,
    // awaiting_validation) stop polling. isPreTerminalStatus is the single
    // source of truth for that decision (frontend/src/utils/jobStatus.ts).
    //
    // Deps are [id] alone. They used to include job?.status, which rebuilt the
    // interval — and fired an extra non-silent fetch — on every transition.
    // Reading jobRef instead means a resume that flips 'suspended' → 'queued'
    // is picked up by the interval already running, with no remount.
    const interval = setInterval(() => {
      // Nobody is looking — spend nothing. onVisible catches them up on return.
      if (document.hidden) return;
      // A job that is genuinely gone (404/403) is not worth retrying, and
      // hammering it would be the polling storm this fix exists to stop.
      if (errorRef.current) return;
      // Still inside the back-off window after a failure.
      if (Date.now() < nextAttemptAtRef.current) return;
      const current = jobRef.current;
      // `!current` keeps retrying a first load that failed transiently —
      // otherwise a tab woken with no network stayed broken until a reload.
      if (!current || isPreTerminalStatus(current.status)) {
        fetchJobDetail(true); // Silent refresh
      }
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [id]);

  const fetchJobDetail = async (silent = false) => {
    // One request at a time. Each poll used to be able to outlive its own 3 s
    // interval, so requests stacked up faster than the server drained them and
    // held every gunicorn thread (ADR-046).
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      if (!silent && !jobRef.current) setLoading(true);
      const response = await jobsAPI.get(id!);
      setJob(response.data);
      // A success clears a previous failure. Without this the page latched:
      // `error` was never reset, and the render branch is `error || !job`, so
      // one failed poll out of hundreds showed "Job Not Found" forever while
      // later polls kept succeeding.
      setError('');
      setUnreachable(false);
      // Recovered — drop straight back to the normal cadence, so a job that had
      // one bad minute is not left updating once every 30 s.
      failureCountRef.current = 0;
      nextAttemptAtRef.current = 0;
    } catch (err: unknown) {
      if (isDefinitivelyGone(errorStatus(err))) {
        setError(extractApiError(err, 'Failed to fetch job details'));
      } else {
        // Transport failure, 5xx, or a backend mid-restart. Keep what is on
        // screen and let the interval retry — more slowly each time, because a
        // server that is already struggling must not receive the same 20
        // requests a minute (ADR-048).
        setUnreachable(true);
        failureCountRef.current += 1;
        nextAttemptAtRef.current = Date.now() + Math.min(
          MAX_POLL_BACKOFF_MS,
          POLL_INTERVAL_MS * 2 ** (failureCountRef.current - 1),
        );
      }
    } finally {
      inFlightRef.current = false;
      if (!silent) setLoading(false);
    }
  };

  const isDimensionJob = job?.pipeline_type === 'dimensions' || job?.pipeline_type === 'dimensions_verification';
  // Which endpoint the Download Results button must hit. Only part_category
  // populates processing_details['validation_queue'], the sole source for
  // /export-validation/; every other pipeline's deliverable is the merged
  // results file behind /download/. Routing them all through the exporter
  // (ea051c0) handed material/generic/analytics users a header-only
  // part-category workbook containing none of their data.
  // Keyed on pipeline_type alone, not the isPartCategoryJob type guard: that
  // guard also demands processing_details be present, and a job missing it
  // would silently fall through to the wrong endpoint again.
  const usesValidationExport = job?.pipeline_type === 'part_category';

  const handleDownload = async () => {
    if (!job) return;
    if (isDimensionJob) return handleDownloadCsv();
    setActiveDownload('results');
    try {
      const response = await jobsAPI.download(job.id);
      if (response.data && typeof response.data === 'object' && 'download_url' in response.data) {
        const { download_url, filename } = response.data;
        triggerAnchorDownload(download_url, filename || job.result_filename || `job_${job.id}_results.csv`);
      } else {
        triggerBlobDownload(response.data, job.result_filename || `job_${job.id}_results.csv`, 'text/csv');
      }
    } catch (error) {
      console.error('Download failed:', error);
      toast({ title: "Couldn't download the results", variant: 'error' });
    } finally {
      setActiveDownload(null);
    }
  };

  const handleDownloadCsv = async () => {
    if (!job) return;
    setActiveDownload('csv');
    try {
      const response = await dimensionsAPI.downloadCsv(String(job.id));
      triggerBlobDownload(response.data, `dimension_export_${String(job.id).slice(0, 8)}.zip`, 'application/zip');
    } catch (error) {
      console.error('CSV download failed:', error);
      toast({ title: "Couldn't download the CSV", variant: 'error' });
    } finally {
      setActiveDownload(null);
    }
  };

  const handleDownloadExcel = async () => {
    if (!job) return;
    setActiveDownload('excel');
    try {
      const response = await dimensionsAPI.downloadExcel(String(job.id));
      triggerBlobDownload(
        response.data,
        `dimension_audit_${String(job.id).slice(0, 8)}.xlsx`,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      );
    } catch (error) {
      console.error('Excel download failed:', error);
      toast({ title: "Couldn't download the audit report", variant: 'error' });
    } finally {
      setActiveDownload(null);
    }
  };

  const handleDownloadPartial = async () => {
    if (!job) return;
    setActiveDownload('partial');
    try {
      const response = await jobsAPI.downloadPartial(job.id);
      if (response.data && typeof response.data === 'object' && 'download_url' in response.data) {
        const { download_url, filename } = response.data as { download_url: string; filename: string };
        triggerAnchorDownload(download_url, filename || `job_${job.id}_partial_results.csv`);
      } else {
        triggerBlobDownload(response.data, `job_${job.id}_partial_results.csv`, 'text/csv');
      }
    } catch (error) {
      console.error('Partial download failed:', error);
      toast({ title: "Nothing to download yet", description: 'This run has not finished a batch.', variant: 'warning' });
    } finally {
      setActiveDownload(null);
    }
  };

  const handlePause = async () => {
    if (!job) return;
    const ok = await confirm({
      title: 'Pause this run?',
      description: 'Progress is saved, and you can resume from here later.',
      confirmText: 'Pause',
    });
    if (!ok) return;

    try {
      await jobsAPI.pause(job.id);
      await fetchJobDetail();
    } catch (error) {
      console.error('Pause failed:', error);
      toast({ title: "Couldn't pause that run", variant: 'error' });
    }
  };

  const handleCancel = async () => {
    // Cancelling a job at the review step throws away results that were already
    // paid for and can no longer be approved or downloaded, so the warning says
    // so rather than reusing the generic one.
    const reviewWarning = job?.status === 'awaiting_validation'
      ? ' The suggested categories will no longer be available to approve or download.'
      : '';
    if (!job) return;
    const ok = await confirm({
      title: 'Cancel this run?',
      description: `This cannot be undone and the run cannot be resumed.${reviewWarning}`,
      confirmText: 'Cancel run',
      cancelText: 'Keep running',
      destructive: true,
    });
    if (!ok) return;

    try {
      await jobsAPI.cancel(job.id);
      await fetchJobDetail();
    } catch (error) {
      console.error('Cancel failed:', error);
      toast({ title: "Couldn't cancel that run", variant: 'error' });
    }
  };

  const handleDownloadExport = async (draft = false) => {
    if (!job) return;
    setActiveDownload('export');
    try {
      const response = await jobsAPI.exportValidation(job.id, draft);
      triggerBlobDownload(
        response.data,
        `part_category_export_${String(job.id).slice(0, 8)}.xlsx`,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      );
    } catch (error) {
      console.error('Export download failed:', error);
      toast({ title: "Couldn't download that export", variant: 'error' });
    } finally {
      setActiveDownload(null);
    }
  };

  const handleResume = async () => {
    if (!job) return;

    setResuming(true);
    try {
      const response = await jobsAPI.resume(job.id);
      const resumeData = response.data as unknown as ResumeJobResponse;

      // Backend returns two different shapes depending on job type:
      //   Dimension job (re-queued in place):  { message, id, status, ... }  ← serializer spread
      //   Standard job (new job created):      { message, new_job: { id, ... }, original_job_id, ... }
      //
      // resumeData.id works for dimension (same id), but is UNDEFINED for standard.
      // resumeData.new_job?.id holds the correct new id for standard jobs.
      const newJobId: string | undefined =
        resumeData.new_job?.id != null ? String(resumeData.new_job.id)   // standard resume
        : resumeData.id != null ? String(resumeData.id) : undefined;      // dimension resume

      if (!newJobId) {
        // Unexpected response shape — refresh in place as fallback
        await fetchJobDetail();
        return;
      }

      const isSamePage = String(newJobId) === String(job.id);

      if (isSamePage) {
        // Dimension job: re-queued in place. Navigate would be a no-op.
        // Fetch immediately so local state flips from 'suspended' → 'queued'.
        // The interval is already running and reads jobRef, so it resumes
        // polling on the next tick — it no longer needs the effect to be
        // re-triggered by the status change (PDR-047).
        await fetchJobDetail();
      } else {
        // Standard job: a new job was created. Navigate to it.
        // Component remounts → useEffect runs → fetchJobDetail fires → polling starts.
        navigate(`/jobs/${newJobId}`);
      }
    } catch (error: unknown) {
      console.error('Resume failed:', error);
      toast({ title: extractApiError(error, "Couldn't resume that run"), variant: 'error' });
    } finally {
      setResuming(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <span className={`ml-3 text-lg ${theme.text.secondary}`}>Loading job details...</span>
        </div>
      </div>
    );
  }

  // Only a job that is genuinely gone gets this screen. It used to be the
  // fallback for *every* failure — a timeout, a 500, a dropped connection —
  // which is why coming back to a long-open tab said the job could not be
  // found while the job was running perfectly well (PDR-047).
  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className={`${theme.card.background} rounded-lg shadow-lg p-8 text-center`}>
          <AlertCircle className="w-16 h-16 text-qc-error mx-auto mb-4" />
          <h2 className={`text-2xl font-bold ${theme.text.primary} mb-2`}>Job Not Found</h2>
          <p className={`${theme.text.secondary} mb-6`}>{error || 'The requested job could not be found'}</p>
          <Link
            to="/jobs"
            className="inline-flex items-center px-6 py-3 bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Jobs
          </Link>
        </div>
      </div>
    );
  }

  // Nothing loaded yet and the server is not answering. The page keeps retrying
  // in the background, so this is a wait state and not a dead end — that is why
  // it says so and offers a retry rather than blaming the job.
  if (!job) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div
          className={`${theme.card.background} rounded-lg shadow-lg p-8 text-center`}
          data-testid="job-detail-unreachable"
        >
          <AlertCircle className="w-16 h-16 text-qc-warning mx-auto mb-4" />
          <h2 className={`text-2xl font-bold ${theme.text.primary} mb-2`}>Still connecting</h2>
          <p className={`${theme.text.secondary} mb-6`}>
            We can't reach the server at the moment. This page keeps trying on its
            own, so it will fill in as soon as the connection is back.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => fetchJobDetail()}
              data-testid="job-detail-retry"
              className="inline-flex items-center px-6 py-3 bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try now
            </button>
            <Link
              to="/jobs"
              className={`inline-flex items-center px-6 py-3 rounded-lg transition-colors ${theme.text.secondary}`}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Jobs
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const getStatusBadge = () => {
    switch (job.status) {
      case 'completed':
        return (
          <span className="px-3 py-1 bg-qc-success/10 text-qc-success rounded-full text-sm font-medium flex items-center">
            <CheckCircle className="w-4 h-4 mr-1" />
            Completed
          </span>
        );
      case 'processing':
        return (
          <span className="px-3 py-1 bg-interactive-primary/10 text-interactive-primary rounded-full text-sm font-medium flex items-center">
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing
          </span>
        );
      case 'failed':
        return (
          <span className="px-3 py-1 bg-qc-error/10 text-qc-error rounded-full text-sm font-medium flex items-center">
            <XCircle className="w-4 h-4 mr-1" />
            Failed
          </span>
        );
      case 'queued':
        return (
          <span className="px-3 py-1 bg-bg-subtle text-text-secondary rounded-full text-sm font-medium flex items-center">
            <Clock className="w-4 h-4 mr-1" />
            Queued
          </span>
        );
      case 'cancelled':
        return (
          <span className={`px-3 py-1 ${theme.background.tertiary} text-text-primary rounded-full text-sm font-medium flex items-center`}>
            <StopCircle className="w-4 h-4 mr-1" />
            Cancelled
          </span>
        );
      case 'suspended':
        return (
          <span className="px-3 py-1 bg-qc-warning/10 text-qc-warning rounded-full text-sm font-medium flex items-center" data-testid="badge-suspended">
            <PauseCircle className="w-4 h-4 mr-1" />
            Paused
          </span>
        );
      case 'awaiting_validation':
        return (
          <span className="px-3 py-1 bg-bg-subtle text-text-secondary rounded-full text-sm font-medium flex items-center" data-testid="badge-awaiting-validation">
            <ClipboardCheck className="w-4 h-4 mr-1" />
            Awaiting Validation
          </span>
        );
      case 'completed_with_errors':
        return (
          <span className="px-3 py-1 bg-qc-warning/10 text-qc-warning rounded-full text-sm font-medium flex items-center" data-testid="badge-completed-with-errors">
            <AlertCircle className="w-4 h-4 mr-1" />
            Completed with Errors
          </span>
        );
      default:
        return (
          <span className="px-3 py-1 bg-qc-warning/10 text-qc-warning rounded-full text-sm font-medium flex items-center">
            <Clock className="w-4 h-4 mr-1" />
            Pending
          </span>
        );
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-bg-base">
      {/* Header */}
      <div className="mb-6">
        <Link 
          to="/jobs" 
          className={`inline-flex items-center ${theme.text.secondary} hover:${theme.text.primary} transition-colors mb-4`}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Jobs
        </Link>
        <h2 className={`text-3xl font-bold ${theme.text.primary}`}>Job Details</h2>
      </div>

      {/* The page keeps the last good data when a refresh fails, so it has to
          say the figures may be behind — otherwise a stalled number reads as a
          stalled job. Plain language, no "SSE"/"poll"/"5xx" (CLAUDE.md §10). */}
      {unreachable && (
        <div
          data-testid="job-detail-stale-banner"
          className="mb-6 flex items-center gap-2 rounded-lg border border-qc-warning/30 bg-qc-warning/10 px-4 py-3 text-sm text-qc-warning"
        >
          <Loader2 className="w-4 h-4 animate-spin shrink-0" />
          <span>
            Reconnecting — showing the last update we received. Nothing has been lost.
          </span>
        </div>
      )}

      {/* Job Card */}
      <div className={`${theme.card.background} rounded-lg shadow-lg p-6 mb-6`}>
        {/* Job Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className={`text-lg font-semibold ${theme.text.primary}`}>{job.job_name || job.id}</h3>
            <p className={`text-sm ${theme.text.secondary}`}>{job.filename}</p>
          </div>
          <div className="flex items-center space-x-2">
            {getStatusBadge()}
          </div>
        </div>

        {/* Progress Bar */}
        {(job.status === 'processing' || job.status === 'queued') && (() => {
          // ── Part Category: derive progress from processing_details ──────────
          if (isPartCategoryJob(job)) {
            const d = job.processing_details;
            const stage = d?.current_stage;

            let pct = 0;
            let leftLabel = d?.stage_name ?? 'Processing…';
            let rightLabel = '';
            let barColor = 'bg-interactive-primary';
            let indeterminate = false;

            if (stage === 'write_back') {
              const synced = d?.synced_count ?? 0;
              const excluded = d?.excluded_count ?? 0;
              const total = Math.max((d?.total_parts ?? 0) - excluded, 0);
              pct = total > 0 ? Math.min((synced / total) * 100, 100) : 0;
              leftLabel = `Writing to digiPART — ${pct.toFixed(0)}%`;
              const errCount = d?.write_errors?.length ?? 0;
              rightLabel = `${synced.toLocaleString()} / ${total.toLocaleString()} parts written${errCount > 0 ? ` · ${errCount} failed` : ''}`;
              barColor = 'bg-interactive-primary';
              indeterminate = total === 0;
            } else if (stage === 'ai_categorisation') {
              // Perf Phase 0 / Task 10 fix round: the Stage-4 checkpoint write
              // now batches processing_details.stage_progress on a cadence
              // (every 10 chunks) so it no longer moves every chunk. The
              // per-chunk liveness write instead updates the job.progress
              // model column every chunk (mirrors the Dimensions pipeline),
              // so that is the live source here; processing_details.stage_progress
              // is kept only as a fallback for jobs mid-migration / pre-fix.
              pct = job.progress ?? d?.stage_progress ?? 0;
              const chunksTotal = d?.chunks_total ?? 0;
              const doneChunks = (d?.last_processed_chunk ?? -1) + 1;
              leftLabel = `AI categorisation — ${pct.toFixed(0)}%`;
              rightLabel = chunksTotal > 0 ? `Chunk ${doneChunks} / ${chunksTotal}` : `${pct.toFixed(0)}%`;
              barColor = 'bg-interactive-primary';
              indeterminate = pct === 0;
            } else {
              pct = d?.stage_progress ?? 0;
              rightLabel = `${pct.toFixed(0)}%`;
              barColor = 'bg-interactive-primary/70';
              indeterminate = pct === 0;
            }

            return (
              <div className="mb-4">
                <div className={`flex justify-between text-sm ${theme.text.secondary} mb-1`}>
                  <span>{leftLabel}</span>
                  <span>{rightLabel}</span>
                </div>
                <div className="w-full bg-bg-hover rounded-full h-2 overflow-hidden">
                  {indeterminate ? (
                    <div className={`${barColor} h-2 rounded-full animate-pulse w-1/3`} />
                  ) : (
                    <div
                      className={`${barColor} h-2 rounded-full transition-all duration-500`}
                      style={{ width: `${pct}%` }}
                    />
                  )}
                </div>
                {job.status_message && (
                  <div className={`flex items-center gap-2 mt-2 text-sm ${theme.text.secondary}`}>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{job.status_message}</span>
                  </div>
                )}
              </div>
            );
          }

          // ── Dimensions / generic jobs ─────────────────────────────────────
          const hasRows = (job.total_rows ?? 0) > 0;
          return (
            <div className="mb-4">
              <div className={`flex justify-between text-sm ${theme.text.secondary} mb-1`}>
                <span>Progress: {job.progress.toFixed(1)}%</span>
                {hasRows ? (
                  <span>{(job.processed_rows || 0).toLocaleString()} / {(job.total_rows || 0).toLocaleString()} rows</span>
                ) : (
                  <span>Processing…</span>
                )}
              </div>
              <div className="w-full bg-bg-hover rounded-full h-2 overflow-hidden">
                {job.progress > 0 ? (
                  <div
                    className="bg-interactive-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${job.progress}%` }}
                  />
                ) : (
                  <div className="bg-interactive-primary h-2 rounded-full animate-pulse w-1/3" />
                )}
              </div>
              {job.status_message && (
                <div className={`flex items-center gap-2 mt-2 text-sm ${theme.text.secondary}`}>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{job.status_message}</span>
                </div>
              )}
            </div>
          );
        })()}

        {/* Dimension Processing Status (for dimensions pipeline) */}
        {isDimensionProcessingStatus(job) && (
          <div className="mb-4">
            <DimensionProcessingStatusComponent job={job} />
          </div>
        )}

        {/* Part Category Processing Status */}
        {isPartCategoryJob(job) && (
          <div className="mb-4">
            <PartCategoryProcessingStatusComponent job={job} />
          </div>
        )}

        {/* Job Stats */}
        {isDimensionJob ? (
          /* Dimensions: Created + Total Rows + Input Tokens + Output Tokens + AI Cost (5 items) */
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
            <div>
              <p className="text-xs text-text-tertiary">Created</p>
              <p className={`text-sm font-medium ${theme.text.primary}`}>
                {new Date(job.created_at).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-text-tertiary">Total Rows</p>
              <p className={`text-sm font-medium ${theme.text.primary}`}>
                {(job.total_rows || 0).toLocaleString()}
              </p>
            </div>
            {job.processing_details?.stats && (
              <>
                <div>
                  <p className="text-xs text-text-tertiary">Input Tokens</p>
                  <p className={`text-sm font-medium ${theme.text.primary}`}>
                    {(job.processing_details.stats.total_input_tokens || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-text-tertiary">Output Tokens</p>
                  <p className={`text-sm font-medium ${theme.text.primary}`}>
                    {(job.processing_details.stats.total_output_tokens || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-text-tertiary">AI Cost</p>
                  <p className={`text-sm font-medium ${theme.text.primary}`}>
                    S${(job.processing_details.stats.total_cost_sgd || 0).toFixed(4)}
                    <span className="text-xs text-text-tertiary ml-1">
                      (${(job.processing_details.stats.total_cost_usd || 0).toFixed(4)} USD)
                    </span>
                  </p>
                </div>
              </>
            )}
          </div>
        ) : job.pipeline_type === 'part_category' ? (
          /* Part Category: Created + Total Parts (2 items) */
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-xs text-text-tertiary">Created</p>
              <p className={`text-sm font-medium ${theme.text.primary}`}>
                {new Date(job.created_at).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-text-tertiary">Total Parts</p>
              <p className={`text-sm font-medium ${theme.text.primary}`}>
                {(job.processing_details?.total_parts ?? job.total_rows ?? 0).toLocaleString()}
              </p>
            </div>
          </div>
        ) : (
          /* Standard pipeline: Created + Total Rows + Cost (SGD) + Tokens (4 items) */
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <p className="text-xs text-text-tertiary">Created</p>
              <p className={`text-sm font-medium ${theme.text.primary}`}>
                {new Date(job.created_at).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-text-tertiary">Total Rows</p>
              <p className={`text-sm font-medium ${theme.text.primary}`}>
                {(job.total_rows || 0).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-text-tertiary">Cost (SGD)</p>
              <p className={`text-sm font-medium ${theme.text.primary}`}>
                ${(parseFloat(job.cost_sgd as any) || 0).toFixed(4)}
              </p>
            </div>
            <div>
              <p className="text-xs text-text-tertiary">Tokens</p>
              <p className={`text-sm font-medium ${theme.text.primary}`}>
                {(job.total_tokens || 0).toLocaleString()}
              </p>
            </div>
          </div>
        )}
        {/* Suspended Banner — shown for paused jobs (session expiry, timeout, quota, worker lost, user paused) */}
        {job.status === 'suspended' && (
          <div className="mb-4 p-4 bg-qc-warning/10 border border-qc-warning/30 rounded-lg" data-testid="suspended-banner">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-qc-warning mb-1 flex items-center gap-2">
                  <PauseCircle className="w-4 h-4" />
                  {job.suspension_code === 'USER_PAUSED'
                    ? 'Job Paused — Ready to Resume'
                    : 'Job Paused — Action Required'}
                </h4>
                <p className="text-sm text-qc-warning mb-3">
                  {job.suspension_code === 'USER_PAUSED'
                    ? 'You paused this job. Resume it to continue processing from where it left off.'
                    : job.error_message || 'This job was paused. Resume the job to continue from where it left off.'}
                </p>
                {job.processing_details?.stats && (
                  <p className="text-xs text-qc-warning mb-3">
                    Progress at pause: {(job.progress ?? 0).toFixed(1)}% &mdash; cost incurred so far: S$
                    {(job.processing_details.stats.total_cost_sgd || 0).toFixed(4)}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 mt-1">
              {/* Only show DigiPART reconnect for session-expiry suspensions */}
              {job.suspension_code === 'DIGIPART_SESSION_EXPIRED' && (
                <Link
                  to="/profile?tab=digipart"
                  className="inline-flex items-center px-4 py-2 bg-qc-warning hover:bg-qc-warning/90 text-text-inverse text-sm font-medium rounded-lg transition-colors"
                  data-testid="reconnect-digipart-btn"
                >
                  Reconnect DigiPART
                </Link>
              )}
              <ResumeJobButton
                onClick={handleResume}
                loading={resuming}
                canResume={job.can_resume ?? false}
                data-testid="resume-job-btn"
              />
            </div>
          </div>
        )}

        {job.error_message && job.status !== 'suspended' && (
          <div className={`mb-4 p-3 ${theme.status.error.background} border border-qc-error/30 rounded-lg`}>
            <p className="text-sm text-qc-error">
              <strong>Error:</strong> {job.error_message}
            </p>
          </div>
        )}

        {/* Awaiting Validation Banner — part_category pipeline after Stage 4 */}
        {job.status === 'awaiting_validation' && (
          <div className="mb-4 p-4 bg-bg-subtle border border-border rounded-lg" data-testid="awaiting-validation-banner">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-text-secondary mb-1 flex items-center gap-2">
                  <ClipboardCheck className="w-4 h-4" />
                  Review & Approve — Human Validation Required
                </h4>
                <p className="text-sm text-text-secondary mb-2">
                  AI categorisation is complete. Review the proposed categories before writing to digiPART.
                </p>
                {job.processing_details && (
                  <div className="flex gap-4 text-xs text-text-secondary">
                    <span>{(job.processing_details.unreviewed_count ?? 0).toLocaleString()} unreviewed</span>
                    <span>{(job.processing_details.edited_count ?? 0).toLocaleString()} edited</span>
                    <span>{(job.processing_details.excluded_count ?? 0).toLocaleString()} excluded</span>
                  </div>
                )}
              </div>
              <Link
                to={`/jobs/${job.id}/validate`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
                data-testid="open-validation-queue-btn"
              >
                <ClipboardCheck className="w-4 h-4" />
                Open Validation Queue →
              </Link>
            </div>
          </div>
        )}

        {/* Part Category Completion Banner — completed or completed_with_errors */}
        {(job.status === 'completed' || job.status === 'completed_with_errors') && job.pipeline_type === 'part_category' && (
          <div
            className={`mb-4 p-4 rounded-lg border ${
              job.status === 'completed'
                ? 'bg-qc-success/10 border-qc-success/30'
                : 'bg-qc-warning/10 border-qc-warning/30'
            }`}
            data-testid="part-category-completion-banner"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h4 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${
                  job.status === 'completed'
                    ? 'text-qc-success'
                    : 'text-qc-warning'
                }`}>
                  {job.status === 'completed'
                    ? <><CheckCircle className="w-4 h-4" /> Part Category Write-Back Complete</>
                    : <><AlertCircle className="w-4 h-4" /> Part Category Write-Back Complete (with errors)</>
                  }
                </h4>
                {job.processing_details && (
                  <div className={`text-sm space-y-0.5 ${
                    job.status === 'completed' ? 'text-qc-success' : 'text-qc-warning'
                  }`}>
                    <p>{(job.processing_details.total_parts ?? job.total_rows ?? 0).toLocaleString()} parts processed</p>
                    <p className="pl-4">├── {(job.processing_details.synced_count ?? 0).toLocaleString()} written to digiPART successfully</p>
                    <p className="pl-4">├── {(job.processing_details.excluded_count ?? 0).toLocaleString()} excluded by reviewer</p>
                    <p className="pl-4">└── {(job.processing_details.write_errors?.length ?? 0).toLocaleString()} failed</p>
                  </div>
                )}
              </div>
            </div>

            {/* Collapsible error log */}
            {((job.processing_details?.write_errors as WritebackError[] | undefined)?.length ?? 0) > 0 ? (
              <div className="mt-3">
                <button
                  onClick={() => setErrorLogOpen(o => !o)}
                  className={`flex items-center gap-1 text-xs font-medium ${
                    job.status === 'completed_with_errors'
                      ? 'text-qc-warning'
                      : 'text-qc-success'
                  }`}
                  data-testid="toggle-error-log-btn"
                >
                  {errorLogOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  {errorLogOpen ? 'Hide' : 'View'} Error Log ({job.processing_details!.write_errors!.length} errors)
                </button>
                {errorLogOpen && (
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-qc-warning/10 text-qc-warning">
                          <th className="text-left px-2 py-1.5 font-medium">Part Number</th>
                          <th className="text-left px-2 py-1.5 font-medium">Part Name</th>
                          <th className="text-left px-2 py-1.5 font-medium">Error Code</th>
                          <th className="text-left px-2 py-1.5 font-medium">Detail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(job.processing_details!.write_errors as WritebackError[]).map((err) => (
                          <tr key={err.part_id} className="border-t border-qc-warning/30">
                            <td className="px-2 py-1.5 font-mono">{err.part_number}</td>
                            <td className="px-2 py-1.5">{err.part_name}</td>
                            <td className="px-2 py-1.5">{err.error_code}</td>
                            <td className="px-2 py-1.5">{err.detail}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : null}
            {/* Cost Breakdown (T-71b) */}
            {isPartCategoryJob(job) && (() => {
              const pcd = job.processing_details as PartCategoryProcessingDetails | undefined;
              if (!pcd?.cost) return null;
              return (
                <PartCategoryCostBreakdown
                  cost={pcd.cost}
                  completed={job.status === 'completed'}
                />
              );
            })()}
          </div>
        )}

        {/* Checkpoint Info - Show for failed/cancelled jobs with checkpoint */}
        {job.can_resume && job.checkpoint_info?.exists && (
          <div className="mb-4 p-4 bg-qc-warning/10 border border-qc-warning/30 rounded-lg">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-qc-warning mb-2">
                  Job Failed with Partial Progress
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                  <div>
                    <p className="text-qc-warning">Progress</p>
                    <p className="font-semibold text-qc-warning">
                      {(job.checkpoint_info.progress_percentage ?? 0).toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-qc-warning">Chunks Completed</p>
                    <p className="font-semibold text-qc-warning">
                      {job.checkpoint_info.last_completed_chunk} / {job.total_chunks}
                    </p>
                  </div>
                  <div>
                    <p className="text-qc-warning">Cost Spent</p>
                    <p className="font-semibold text-qc-warning">
                      ${job.checkpoint_info.cost_spent_sgd} SGD
                    </p>
                  </div>
                  <div>
                    <p className="text-qc-warning">Last Checkpoint</p>
                    <p className="font-semibold text-qc-warning">
                      {job.checkpoint_info.updated_at ? new Date(job.checkpoint_info.updated_at).toLocaleString() : '—'}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-qc-warning">
                  Resume this job to continue processing from where it left off. You won't be charged again for already-processed chunks.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap justify-end gap-2 mt-6">
          {/* Download In-Progress Results — any non-completed job with processed rows */}
          {job.has_partial_results && job.status !== 'completed' && (
            <DownloadButton
              onClick={handleDownloadPartial}
              loading={activeDownload === 'partial'}
              icon={Download}
              label={`Download Progress (${(job.processed_rows ?? 0).toLocaleString()} rows)`}
              data-testid="download-partial-btn"
              className="px-4 py-2 bg-qc-warning hover:bg-qc-warning/90 text-text-inverse rounded-lg flex items-center space-x-2 transition-colors disabled:opacity-50"
            />
          )}

          {/* Download Results — completed standard jobs */}
          {job.status === 'completed' && !isDimensionJob && (
            <DownloadButton
              onClick={usesValidationExport ? () => handleDownloadExport(false) : handleDownload}
              loading={activeDownload === (usesValidationExport ? 'export' : 'results')}
              icon={Download}
              label="Download Results"
              data-testid="download-export-btn"
              className="px-4 py-2 bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse rounded-lg flex items-center space-x-2 transition-colors disabled:opacity-50"
            />
          )}

          {/* Download CSV Export — completed dimension jobs */}
          {job.status === 'completed' && isDimensionJob && (
            <DownloadButton
              onClick={handleDownloadCsv}
              loading={activeDownload === 'csv'}
              icon={FileText}
              label="Download CSV Export"
              data-testid="download-csv-btn"
              className="px-4 py-2 bg-qc-success hover:bg-qc-success/90 text-text-inverse rounded-lg flex items-center space-x-2 transition-colors disabled:opacity-50"
            />
          )}

          {/* Download Excel Audit — completed dimension jobs */}
          {job.status === 'completed' && isDimensionJob && (
            <DownloadButton
              onClick={handleDownloadExcel}
              loading={activeDownload === 'excel'}
              icon={FileText}
              label="Download Excel Report"
              data-testid="download-excel-btn"
              className="px-4 py-2 bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse rounded-lg flex items-center space-x-2 transition-colors disabled:opacity-50"
            />
          )}

          {/* Pause Job — saves checkpoint; job becomes resumable */}
          {(job.status === 'processing' || job.status === 'queued') && (
            <button
              onClick={handlePause}
              data-testid="pause-job-btn"
              className="px-4 py-2 bg-qc-warning hover:bg-qc-warning/90 text-text-inverse rounded-lg flex flex-col items-center transition-colors"
            >
              <div className="flex items-center space-x-2">
                <PauseCircle className="w-4 h-4" />
                <span>Pause Job</span>
              </div>
              <span className="text-xs opacity-80 leading-tight mt-0.5">Progress will be saved</span>
            </button>
          )}

          {/* Cancel Job — hard kill, job is NOT resumable. Also the only exit
              from the review step (PDR-011) and from a suspended job whose resume
              cannot help it (PDR-032): a generic job suspended before its first
              checkpoint showed a disabled Resume and no Cancel, i.e. a dead end.
              Driven by the server's can_cancel so this list cannot drift from the
              rule the endpoint enforces; the fallback keeps older API responses
              working. */}
          {(job.can_cancel ?? ['processing', 'queued', 'awaiting_validation', 'suspended']
            .includes(job.status)) && (
            <button
              onClick={handleCancel}
              data-testid="cancel-job-btn"
              className="px-4 py-2 bg-qc-error hover:bg-qc-error/90 text-text-inverse rounded-lg flex flex-col items-center transition-colors"
            >
              <div className="flex items-center space-x-2">
                <StopCircle className="w-4 h-4" />
                <span>Cancel Job</span>
              </div>
              <span className="text-xs opacity-80 leading-tight mt-0.5">Cannot be resumed</span>
            </button>
          )}

          {/* Resume Job — for failed / cancelled / suspended; disabled when no checkpoint */}
          {['failed', 'cancelled', 'suspended'].includes(job.status) && (
            <ResumeJobButton
              onClick={handleResume}
              loading={resuming}
              canResume={job.can_resume ?? false}
              data-testid="resume-job-btn-actions"
              className="px-4 py-2 bg-qc-success hover:bg-qc-success/90 text-text-inverse rounded-lg flex items-center space-x-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default JobDetail;
